// The shape the backend actually returns.
//
// Written against the live payload rather than hoped for: every field here was
// observed on /api/casinobias/data. Anything the backend may omit is optional,
// and every numeric reading is `number | null | undefined` because "this
// tracker does not publish this figure" is the single most common state in the
// dataset and the type has to make that impossible to ignore.

/** The four trackers, by the ids the backend uses. */
export const TRACKER_IDS = ['circus', 'fairgambling', 'tanzanite', 'stakesim'] as const;
export type TrackerId = (typeof TRACKER_IDS)[number];

export const TRACKER_NAMES: Record<TrackerId, string> = {
  circus: 'circus.fyi',
  fairgambling: 'FairGambling',
  tanzanite: 'Tanzanite',
  stakesim: 'StakeSim',
};

/** Time windows the backend computes. Not every window has every tracker. */
export type WindowKey = '1d' | '7d' | '14d' | '30d' | '90d' | '365d';

/**
 * One tracker's readings for one casino.
 *
 * Every field is optional: a tracker publishing deposit volume says nothing
 * about whether it publishes a returning-player rate, and treating a missing
 * figure as zero is the mistake this whole site exists to avoid.
 */
export interface Reading {
  value?: number;
  count?: number;
  depositors?: number;
  withdrawals?: number;
  withdrawalCount?: number;
  withdrawers?: number;
  avgDeposit?: number;
  avgWithdrawal?: number;
  netFlow?: number;
  share?: number;
  rank?: number;
  ftd?: number;
  newDeposits?: number;
  largestDeposit?: number;
  largestWithdrawal?: number;
  returnRate?: number;
  returningWallets?: number;
  wallets?: number;
  change?: number;
  depositsChange?: number;
  withdrawalsChange?: number;
  depositorsChange?: number;
}

/** Every numeric field a Reading can carry — the metric keys, as a type. */
export type MetricKey = {
  [K in keyof Reading]-?: Reading[K] extends number | undefined ? K : never;
}[keyof Reading];

/** One tracker's chain split for one casino. */
export interface ChainSplit {
  chain: string;
  volume?: number;
  deposits?: number;
  withdrawals?: number;
}

/** What a tracker states about a casino, as opposed to measures. */
export interface CasinoMeta {
  license?: string;
  foundedYear?: number;
  houseEdge?: number;
  sportsHouseEdge?: number;
  siteType?: string;
  url?: string;
  fairSlug?: string;
}

export interface CasinoExtra {
  chains?: ChainSplit[] | null;
  balances?: unknown;
  meta?: CasinoMeta | null;
  distribution?: unknown;
  daily?: unknown;
}

export interface CasinoRow {
  key: string;
  name: string;
  names?: Partial<Record<TrackerId, string>>;
  logos?: Partial<Record<TrackerId, string>>;
  by: Partial<Record<TrackerId, Reading>>;
  /** Per-tracker detail: each one's own chain split and stated facts. */
  extra?: Partial<Record<TrackerId, CasinoExtra>>;
  /** Trackers that list this casino at all. */
  reportedBy: TrackerId[];
  /** Trackers that list it *and* are counted into the median (tier 1 only). */
  consensusBy: TrackerId[];
  /** Median deposit volume across `consensusBy`. */
  consensus?: number;
  /** (highest − lowest) / median, as a percentage. Null when only one reading. */
  spreadPct?: number | null;
  dev?: Partial<Record<TrackerId, number | null>>;
  normShare?: Partial<Record<TrackerId, number>>;
  /** Position within the set every counted tracker covers. */
  commonRank?: Partial<Record<TrackerId, number>>;
  shareConsensus?: number;
  shareDev?: Partial<Record<TrackerId, number>>;
}

export interface VolumeWindow {
  label: string;
  /** Trackers with data in this window. */
  sources: TrackerId[];
  /** Of those, the ones allowed to move a median. */
  consensusSources: TrackerId[];
  rows: CasinoRow[];
  totals?: Partial<Record<TrackerId, { value?: number; casinos?: number }>>;
  /** Totals restricted to casinos every consensus source covers. */
  commonTotals?: Partial<Record<TrackerId, number>>;
  commonCount?: number;
  coverage?: Partial<Record<TrackerId, { tracked?: number; unique?: string[] }>>;
  /** Volume per chain, per tracker. Chain codes as published. */
  chains?: Record<string, Partial<Record<TrackerId, number>>>;
  /** The subset of `sources` that publishes a chain breakdown. */
  chainSources?: TrackerId[];
  /** How each counted tracker leans against the median, over casinos two or more count. */
  lean?: Partial<Record<TrackerId, Lean>>;
}

/** One counted tracker's lean: deviations are percent from the casino's median. */
export interface Lean {
  n: number;
  meanDev?: number | null;
  medianDev?: number | null;
  topTenDev?: number | null;
  weightedDev?: number | null;
  above?: number;
  below?: number;
  commonTotal?: number | null;
}

export interface BalanceRow {
  key: string;
  name: string;
  by: Partial<Record<TrackerId, { value?: number; wallets?: number }>>;
  reportedBy: TrackerId[];
  consensus?: number;
  spreadPct?: number | null;
}

export interface Balances {
  sources: TrackerId[];
  rows: BalanceRow[];
  totals?: Partial<Record<TrackerId, { value?: number }>>;
  coverage?: Partial<Record<TrackerId, { tracked?: number }>>;
}

export interface SentimentRow {
  key: string;
  name: string;
  logo?: string;
  score?: number;
  organicScore?: number;
  confidence?: number;
  label?: string;
  sourceCount?: number;
  trustpilot?: number;
  trustpilotReviews?: number;
  casinoGuru?: number;
  guruReviews?: number;
  bitcointalk?: number;
  circusScore?: number;
  fgScore?: number;
  wagerx?: number;
  casinoRankr?: number;
  rakebackPct?: number;
}

export interface SourceStatus {
  id: TrackerId;
  label: string;
  url: string;
  /** 1 is counted into medians; 2 is shown for reference only. */
  tier: 1 | 2;
  ok: boolean;
  error?: string | null;
  stale?: boolean;
  durationMs?: number;
  scope?: string;
  note?: string;
}

/** What the trackers state about casinos (licence, founding year, ...), and where they conflict. */
export interface ProfileField {
  key: string;
  label: string;
  type: string;
  sources: TrackerId[];
  /** Casinos where two or more trackers state this field. */
  compared: number;
  /** Of those, the ones where they state different things. */
  conflicts: number;
  compare?: boolean;
}
export interface ProfileRow {
  key: string;
  name: string;
  reportedBy: TrackerId[];
  fields: { key: string; label: string; type: string; values: Partial<Record<TrackerId, string | number | boolean | null>>; agree: boolean | null }[];
}
export interface Profiles {
  sources: TrackerId[];
  fields: ProfileField[];
  rows: ProfileRow[];
  universe?: number;
  comparable?: number;
  conflicting?: number;
}

export interface Payload {
  delivery?: 'backend' | 'saved';
  scrapedAt: string;
  durationMs?: number;
  windows: WindowKey[];
  sources: SourceStatus[];
  analysis: {
    volume: Partial<Record<WindowKey, VolumeWindow>>;
    balances?: Balances;
    sentiment?: { rows: SentimentRow[] };
    profiles?: Profiles;
  };
}

/** One scrape, as kept in the history store. */
export interface Snapshot {
  scrapedAt: string;
  sources: Partial<Record<TrackerId, { ok: boolean; casinos?: number }>>;
  /** Keyed by casino, then by `${trackerId}:${window}`. */
  casinos: Record<string, { name: string; v: Record<string, number> }>;
}
